/* iter155 — Ride Mode screen.
 *
 * A full-screen "ambient" view that REPLACES the Discover home content
 * when the user toggles into Ride mode (from the profile dropdown or
 * from the toggle at the bottom of this screen). All ride actions
 * delegate to existing components — this file introduces NO new ride
 * logic:
 *   • Ride button   → fires sendRideRequest() → opens the existing
 *                      WhatsAppActionSheet with type='ride'.
 *   • History button → navigates to /rides/history (existing page).
 *   • Language pill → uses the existing i18n context.
 *   • Profile pill  → renders the existing ProfileDropdown.
 *   • Weather / rates → reuse existing weather + exchange-rate fetches.
 *
 * Additive-only: replaces NO existing page in the router; it is
 * conditionally rendered by Home.jsx based on `useMode()`.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Globe, History as HistoryIcon, Save, Image as ImageIcon, Plane, Cigarette } from "lucide-react";
import OwnerAsCustomerRibbon from "../components/OwnerAsCustomerRibbon";
import { api } from "../lib/api";
import { lsGet, lsSet } from "../lib/safeStorage";
import { useI18n } from "../lib/i18n";
import { fmtTemp, useLangUnit, translateWeatherDesc } from "../lib/weather";
import WeatherForecastModal from "../components/WeatherForecastModal";
import { setMode } from "../lib/appMode";
import { useDiscoverAvailable } from "../lib/discoverAvailability";
import { LogoMark } from "../components/Logo";
import ProfileDropdown from "../components/ProfileDropdown";
import SavedPlacesSheet from "../components/SavedPlacesSheet";
import CigarsBarsSheet from "../components/CigarsBarsSheet";
import RideBgUploadModal from "../components/RideBgUploadModal";
import { getUserBackgroundUrl, subscribeUserBackground } from "../lib/rideBgStore";
import { Link } from "react-router-dom";
import { getMe } from "../lib/api";

const WEATHER_CACHE = "habano.weather.v1";
const RATES_CACHE = "habano.exchange.rates.v1";
const BACKGROUNDS_CACHE = "habano.ride.backgrounds.v1";

export default function RideMode() {
  const { t, lang, setLang } = useI18n();
  const [unit] = useLangUnit();
  const nav = useNavigate();
  // iter170 — Global Discover switch (with per-user admin bypass). When
  // false the Ride↔Discover toggle in the bottom row is hidden.
  const discoverAvailable = useDiscoverAvailable();

  const [user, setUser] = useState(null);
  // iter175 — Same tap-to-open forecast modal used by the Discover
  // WeatherWidget. State + modal reuse the exact same component, so
  // the popup, unit toggle, and layout stay perfectly in sync.
  const [forecastOpen, setForecastOpen] = useState(false);
  const [weather, setWeather] = useState(() => {
    try { return JSON.parse(lsGet(WEATHER_CACHE) || "null"); } catch { return null; }
  });
  const [rates, setRates] = useState(() => {
    try { return JSON.parse(lsGet(RATES_CACHE) || "null"); } catch { return null; }
  });

  // iter161 — Ambient hero background. Uses (in order of priority):
  //   1. User-uploaded background from IndexedDB (static — no rotation).
  //   2. A random image from the admin-managed set fetched from
  //      /api/ride-mode/backgrounds. Rotates on each fresh mount.
  const [userBgUrl, setUserBgUrl] = useState(null);
  const [adminBg, setAdminBg] = useState(null);
  const [savedPlacesOpen, setSavedPlacesOpen] = useState(false);
  const [bgUploadOpen, setBgUploadOpen] = useState(false);
  // iter176 — Cigars & Bars sheet (opened by the new Cigars button in
  // the bottom cluster). Shows businesses with `show_on_ride=true`.
  const [cigarsBarsOpen, setCigarsBarsOpen] = useState(false);

  // Load user's own background (if any) and subscribe to changes.
  useEffect(() => {
    let cancelled = false;
    getUserBackgroundUrl().then((url) => { if (!cancelled) setUserBgUrl(url); });
    const unsub = subscribeUserBackground(() => {
      getUserBackgroundUrl().then((url) => setUserBgUrl(url));
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  // Load admin-managed backgrounds and pick one at random on each mount.
  // We only fetch when there is no user override (user override wins).
  useEffect(() => {
    if (userBgUrl) return;
    // Serve from cache first for offline / instant paint.
    let list = [];
    try { list = JSON.parse(lsGet(BACKGROUNDS_CACHE) || "[]"); } catch { list = []; }
    if (list.length) {
      setAdminBg(list[Math.floor(Math.random() * list.length)]);
    }
    api.get("/ride-mode/backgrounds").then(({ data }) => {
      const items = (data?.backgrounds || []).filter((b) => b.url);
      try { lsSet(BACKGROUNDS_CACHE, JSON.stringify(items)); } catch { /* ignore */ }
      if (items.length) setAdminBg(items[Math.floor(Math.random() * items.length)]);
    }).catch(() => {});
  }, [userBgUrl]);

  const heroBg = userBgUrl || adminBg?.url || null;

  useEffect(() => {
    getMe().then(setUser).catch(() => setUser(null));
    api.get("/weather").then(({ data }) => {
      if (data?.available && data?.current) {
        setWeather(data);
        try { lsSet(WEATHER_CACHE, JSON.stringify(data)); } catch { /* ignore */ }
      }
    }).catch(() => {});
    api.get("/cuba-exchange-rate").then(({ data }) => {
      if (Array.isArray(data?.rates) && data.rates.length) {
        setRates(data);
        try { lsSet(RATES_CACHE, JSON.stringify(data)); } catch { /* ignore */ }
      }
    }).catch(() => {});
  }, []);

  const c = weather?.current;
  const description = translateWeatherDesc(c?.description || "", lang);

  // iter166 — Drop-shadow applied to all hero text ONLY when a background
  // photo is behind it (admin rotation or user upload). Keeps the CSS-art
  // fallback's stark palette untouched.
  const heroTextShadow = heroBg
    ? "0 2px 8px rgba(0,0,0,0.75), 0 1px 3px rgba(0,0,0,0.9)"
    : undefined;

  // iter212 — Driver ETA hint. Poll `/api/drivers/nearby` every 30 s and
  // compute avg distance → pickup ETA at ~20 km/h. Hidden completely when
  // count is zero so the empty state stays clean.
  const [driverEtaHint, setDriverEtaHint] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchDrivers = async () => {
      try {
        const { data } = await api.get(`/drivers/nearby?lat=23.1136&lng=-82.3666&radius_km=3`);
        const list = data.drivers || [];
        if (cancelled) return;
        if (list.length === 0) { setDriverEtaHint(null); return; }
        const avgKm = list.reduce((s, d) => s + (d.distance_km || 0), 0) / list.length;
        // Avg city speed 20 km/h → 3 min per km. Round to whole minutes, min 1.
        const etaMin = Math.max(1, Math.round(avgKm * 3));
        setDriverEtaHint({ count: list.length, etaMin });
      } catch { /* offline-tolerant — hide */ }
    };
    fetchDrivers();
    const id = setInterval(fetchDrivers, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // iter157 — Ride button navigates to /car/map (same target as the
  // bottom-nav RIDE button and the existing pin/CTA elsewhere). Same
  // page, same map, same crosshair, same Get-a-Ride-Here confirm.
  const openRide = () => nav("/car/map");

  const otherLang = lang === "es" ? "en" : "es";
  const otherLabel = otherLang === "en" ? "English" : "Español";

  // iter157 — Format exchange rate rows for ambient display. Reuses the
  // EXACT same fetch + cache key as ExchangeRateStrip on Discover so the
  // data is shared across pages. API payload row shape is
  // `{currency: "USD", rate: <num>, updatedAt}` — key is `currency`, NOT `code`.
  const byCur = Object.fromEntries((rates?.rates || []).map((r) => [r.currency, r]));
  const usd = byCur.USD?.rate;
  const eur = byCur.EUR?.rate;
  const fmtRate = (v) => (v == null ? "—" : Math.round(Number(v)).toString());

  return (
    <main
      data-testid="ride-mode-screen"
      className="fixed inset-0 overflow-hidden"
      style={{
        background: "#000000",
        color: "#F5F1E8",
      }}
    >
      {/* iter216 — Owner-as-customer ribbon (session-only). Rendered
          only when the owner arrived here via their profile toggle. */}
      <div className="absolute top-0 inset-x-0 z-30" style={{ pointerEvents: "auto" }}>
        <OwnerAsCustomerRibbon />
      </div>
      {/* iter161 — Hero background: user upload > admin rotation > CSS art.
       * iter164 — The dark tint layer that used to live inside this div
       * has been removed. The top and bottom scrims (rendered
       * separately below at z-5) now sit ON TOP of the photo, leaving
       * the middle of the frame fully un-tinted. */}
      {heroBg ? (
        <div
          aria-hidden
          data-testid="ride-mode-hero-image"
          className="absolute z-0"
          style={{
            // iter166 — Overshoot the viewport by 120px on top and bottom
            // so the photo bleeds fully past the status bar / home-indicator
            // safe areas on every device, leaving no brown gap.
            top: -120,
            bottom: -120,
            left: 0,
            right: 0,
            backgroundImage: `url(${heroBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      ) : (
        <BackgroundArt />
      )}

      {/* iter162 — Contrast scrims for photo backgrounds. Only render
          when a photo (user or admin) is behind the UI so the CSS
          Capitolio art keeps its stark palette. Top scrim protects
          the header icon row; bottom scrim protects the buttons +
          toggle cluster; the frosted panel behind the wordmark is
          injected inside the ambient stack below. */}
      {heroBg && (
        <>
          <div
            aria-hidden
            data-testid="ride-mode-scrim-top"
            className="absolute inset-x-0 top-0 z-[5] pointer-events-none"
            style={{
              height: 168,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0) 100%)",
            }}
          />
          <div
            aria-hidden
            data-testid="ride-mode-scrim-bottom"
            className="absolute inset-x-0 bottom-0 z-[5] pointer-events-none"
            style={{
              height: 300,
              background:
                "linear-gradient(0deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.7) 45%, rgba(0,0,0,0) 100%)",
            }}
          />
        </>
      )}

      {/* ── Header (same layout as other pages: logo left, actions right) ── */}
      <header
        data-testid="ride-mode-header"
        className="relative flex items-center justify-between px-5 pt-6 pb-2"
        style={{ zIndex: 100 }}
      >
        <div data-testid="ride-mode-logo">
          <LogoMark />
        </div>
        <div className="flex items-center gap-3">
          {/* Language pill — shows the OTHER language, tap switches */}
          <button
            type="button"
            data-testid="ride-mode-lang-pill"
            aria-label={otherLang === "en" ? "Switch to English" : "Cambiar a Español"}
            onClick={() => setLang(otherLang)}
            className="inline-flex items-center gap-1.5"
            style={{
              minHeight: 32,
              padding: "6px 12px",
              borderRadius: 9999,
              border: "1px solid rgba(232,184,75,0.35)",
              background: "rgba(23,23,23,0.7)",
              backdropFilter: "blur(8px)",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12,
              fontWeight: 600,
              color: "#E8B84B",
              letterSpacing: "0.05em",
            }}
          >
            <Globe className="w-3.5 h-3.5" strokeWidth={1.6} />
            {otherLabel}
          </button>
          <ProfileDropdown user={user} testidPrefix="ride-mode" />
        </div>
      </header>

      {/* ── Ambient info stack (city, temp, weather, rates) ─────────── */}
      <div className="relative z-10 flex flex-col items-center pt-6 sm:pt-10 px-6">
        <div
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 40,
            fontWeight: 700,
            color: "rgba(255,255,255,0.95)",
            lineHeight: 1.1,
            textShadow: heroTextShadow,
          }}
          data-testid="ride-mode-city"
        >
          La Habana
        </div>
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "7px",
            color: "rgba(232,184,75,0.95)",
            marginTop: 10,
            textShadow: heroTextShadow,
          }}
          data-testid="ride-mode-country"
        >
          CUBA
        </div>

        {/* iter157 — Ambient temperature. Uses the same
            /api/weather + habano.weather.v1 cache as WeatherWidget on
            Discover so any data available on that page is available
            here. Renders '—°F/°C' placeholder while cache warms. */}
        <button
          type="button"
          data-testid="ride-mode-temp"
          onClick={() => setForecastOpen(true)}
          aria-label={lang === "es" ? "Ver pronóstico del tiempo" : "See weather forecast"}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 52,
            fontWeight: 700,
            color: "rgba(255,255,255,0.95)",
            marginTop: 28,
            lineHeight: 1,
            textShadow: heroTextShadow,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          {c ? fmtTemp(c.temp_f, unit) : `—°${unit.toUpperCase()}`}
        </button>
        {description && (
          <div
            data-testid="ride-mode-weather-desc"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "5px",
              color: "rgba(245,241,232,0.9)",
              marginTop: 12,
              textTransform: "uppercase",
              textShadow: heroTextShadow,
            }}
          >
            {description}
          </div>
        )}

        {/* iter159 — Ambient rates strip. Uses the SAME endpoint +
            cache as ExchangeRateStrip on Discover, and navigates to
            the SAME /exchange-rate detail page on tap. Font bumped
            from 12px→14px with high-contrast rgba 0.9 for legibility
            against the ambient background. */}
        <Link
          to="/exchange-rate"
          data-testid="ride-mode-rates"
          aria-label={lang === "es" ? "Ver tipo de cambio" : "View exchange rate"}
          className="inline-flex items-center gap-3 mt-10 select-none"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            color: "rgba(232,184,75,0.9)",
            letterSpacing: "0.06em",
            padding: "10px 18px",
            borderRadius: 9999,
            border: "1px solid rgba(232,184,75,0.28)",
            background: "rgba(23,23,23,0.55)",
            backdropFilter: "blur(8px)",
            minHeight: 44,
          }}
        >
          <span data-testid="ride-mode-rate-usd">
            <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7, marginRight: 4 }}>USD</span>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{fmtRate(usd)}</span>
          </span>
          <span style={{ opacity: 0.3, fontSize: 12 }}>·</span>
          <span data-testid="ride-mode-rate-eur">
            <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7, marginRight: 4 }}>EUR</span>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{fmtRate(eur)}</span>
          </span>
          <span style={{ opacity: 0.3, fontSize: 12 }}>·</span>
          <span data-testid="ride-mode-rate-cup" style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.08em" }}>CUP</span>
        </Link>
      </div>

      {/* ── Bottom CTAs + mode toggle ─────────────────────────────── */}
      <div
        className="absolute inset-x-0 bottom-0 z-10 px-6 pb-8"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 45%, rgba(0,0,0,0.95) 100%)",
        }}
      >
        <button
          data-testid="ride-mode-request-ride"
          type="button"
          onClick={openRide}
          className="w-full flex items-center justify-center gap-2 font-semibold"
          style={{
            minHeight: 56,
            borderRadius: 4,
            background: "linear-gradient(135deg, #E7C77E 0%, #E8B84B 55%, #A07820 100%)",
            color: "#0B0B0B",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            letterSpacing: "0.05em",
            boxShadow: "0 12px 32px rgba(232,184,75,0.35)",
          }}
        >
          <Car className="w-5 h-5" strokeWidth={1.75} />
          {lang === "es" ? "Solicitar Viaje" : "Request a Ride"}
        </button>

        {/* iter212 — Driver ETA hint (rendered ONLY when ≥1 driver online). */}
        {driverEtaHint && (
          <p
            data-testid="ride-mode-driver-eta"
            className="text-center mt-2 mb-0"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: "#9A9490",
              letterSpacing: "0.01em",
            }}
          >
            {lang === "es"
              ? `${driverEtaHint.count} conductor${driverEtaHint.count === 1 ? "" : "es"} cerca · ${driverEtaHint.etaMin} min promedio`
              : `${driverEtaHint.count} driver${driverEtaHint.count === 1 ? "" : "s"} nearby · avg. ${driverEtaHint.etaMin} min pickup`}
          </p>
        )}

        <button
          data-testid="ride-mode-history"
          type="button"
          onClick={() => nav("/rides/history")}
          className="w-full flex items-center justify-center gap-2 mt-3"
          style={{
            minHeight: 56,
            borderRadius: 4,
            background: "transparent",
            border: "1px solid rgba(232,184,75,0.5)",
            color: "#E8B84B",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            fontWeight: 500,
            letterSpacing: "0.05em",
          }}
        >
          <HistoryIcon className="w-5 h-5" strokeWidth={1.6} />
          {lang === "es" ? "Historial" : "History"}
        </button>

        {/* iter161 — Saved Places (left) · Ride↔Discover toggle · Background (right).
         * iter169 — Icon-to-toggle spacing.
         * iter170 — Small text labels under each circular icon.
         * iter176 — Full 4-button layout: [Places][Background] Toggle
         * [Airport][Cigars]. Ride + History big buttons below this
         * row remain unchanged. All 4 circles share the same styling.
         * iter176-fix — gap reduced to 4px and toggle text hidden to
         * fit within a 390px viewport. Full "Ride / Discover" labels
         * are still visible via the ToggleTrack itself + accessibility
         * label.
         * iter182 — Switched to `justify-content: space-evenly` so
         * the four RideCircle buttons distribute evenly across the
         * full row width. The centred toggle (3rd of 5 flex children)
         * lands exactly between Background and Airport. */}
        <div className="flex items-start mt-6" style={{ justifyContent: "space-evenly" }}>
          {/* Places */}
          <RideCircle
            testid="ride-mode-saved-places-btn"
            label={lang === "es" ? "Lugares" : "Places"}
            labelTestid="ride-mode-saved-places-label"
            ariaLabel={lang === "es" ? "Lugares guardados" : "Saved places"}
            onClick={() => setSavedPlacesOpen(true)}
            Icon={Save}
            heroTextShadow={heroTextShadow}
          />
          {/* Background */}
          <RideCircle
            testid="ride-mode-bg-upload-btn"
            label={lang === "es" ? "Fondo" : "Background"}
            labelTestid="ride-mode-bg-upload-label"
            ariaLabel={lang === "es" ? "Personalizar fondo" : "Customize background"}
            onClick={() => setBgUploadOpen(true)}
            Icon={ImageIcon}
            heroTextShadow={heroTextShadow}
          />

          {discoverAvailable && (
            <button
              type="button"
              data-testid="ride-mode-toggle-discover"
              onClick={() => setMode("discover")}
              className="flex items-center justify-center self-center"
              aria-label={lang === "es" ? "Cambiar a Descubrir" : "Switch to Discover"}
              style={{
                minHeight: 48,
                minWidth: 48,
                padding: "0 6px",
                background: "transparent",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 10,
                letterSpacing: "0.05em",
                gap: 4,
                marginLeft: 0, marginRight: 0,
              }}
            >
              <span style={{ color: "#E8B84B", fontWeight: 600 }}>
                {lang === "es" ? "Viaje" : "Ride"}
              </span>
              <ToggleTrack on={false} />
              <span style={{ color: "rgba(245,241,232,0.6)" }}>
                {lang === "es" ? "Desc." : "Disc."}
              </span>
            </button>
          )}

          {/* Airport */}
          <RideCircle
            testid="ride-mode-airport-btn"
            label={lang === "es" ? "Aeropuerto" : "Airport"}
            labelTestid="ride-mode-airport-label"
            ariaLabel={lang === "es" ? "Aeropuerto" : "Airport"}
            onClick={() => nav("/airport")}
            Icon={Plane}
            heroTextShadow={heroTextShadow}
          />
          {/* Cigars & Bars */}
          <RideCircle
            testid="ride-mode-cigars-btn"
            label={lang === "es" ? "Cigarros" : "Cigars"}
            labelTestid="ride-mode-cigars-label"
            ariaLabel={lang === "es" ? "Cigarros y bares" : "Cigars and bars"}
            onClick={() => setCigarsBarsOpen(true)}
            Icon={Cigarette}
            heroTextShadow={heroTextShadow}
          />
        </div>
      </div>

      <SavedPlacesSheet isOpen={savedPlacesOpen} onClose={() => setSavedPlacesOpen(false)} />
      <CigarsBarsSheet isOpen={cigarsBarsOpen} onClose={() => setCigarsBarsOpen(false)} />
      <RideBgUploadModal
        isOpen={bgUploadOpen}
        hasCurrent={!!userBgUrl}
        onClose={() => setBgUploadOpen(false)}
        onSaved={() => { /* subscribeUserBackground refresh handled in effect */ }}
      />
      {/* iter175 — Same modal Discover's WeatherWidget opens. */}
      <WeatherForecastModal
        open={forecastOpen}
        onOpenChange={setForecastOpen}
        weather={weather}
        unit={unit}
        toggleUnit={() => {}}
      />
    </main>
  );
}

/* ── Ambient background art ──────────────────────────────────────────
 * All CSS/SVG — no external images. Draws a subtle gold moon top-right,
 * scattered stars, and a stylised Capitolio silhouette centered at the
 * bottom horizon.
 */
function BackgroundArt() {
  return (
    <div aria-hidden className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
      {/* Moon */}
      <div
        style={{
          position: "absolute",
          top: 90,
          right: 50,
          width: 46,
          height: 46,
          borderRadius: "50%",
          background: "radial-gradient(circle at 30% 30%, #F5E1A6 0%, #E8B84B 55%, rgba(232,184,75,0.15) 100%)",
          boxShadow: "0 0 50px rgba(232,184,75,0.35)",
          opacity: 0.85,
        }}
      />
      {/* Stars — deterministic positions so SSR/CSR don't diverge. */}
      {STAR_POSITIONS.map((s, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: `${s.y}%`,
            left: `${s.x}%`,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.55)",
            boxShadow: "0 0 4px rgba(255,255,255,0.65)",
            opacity: s.o,
          }}
        />
      ))}
      {/* Capitolio silhouette — CSS-drawn using a wide dome + columns + steps. */}
      <div
        style={{
          position: "absolute",
          bottom: 190,
          left: "50%",
          transform: "translateX(-50%)",
          width: 300,
          filter: "drop-shadow(0 -10px 32px rgba(232,184,75,0.08))",
        }}
      >
        <svg viewBox="0 0 300 130" width="300" height="130" xmlns="http://www.w3.org/2000/svg">
          {/* Dome */}
          <path d="M120,55 Q150,10 180,55 L180,60 L120,60 Z" fill="#2b1810" opacity="0.9" />
          {/* Spire */}
          <line x1="150" y1="18" x2="150" y2="0" stroke="#2b1810" strokeWidth="1.5" />
          {/* Drum */}
          <rect x="118" y="58" width="64" height="10" fill="#2b1810" opacity="0.9" />
          {/* Colonnade */}
          <rect x="60" y="68" width="180" height="8" fill="#2b1810" opacity="0.9" />
          {/* Columns (thin verticals) */}
          {Array.from({ length: 14 }).map((_, i) => (
            <rect key={i} x={64 + i * 12.6} y="76" width="1.5" height="22" fill="#3b2317" opacity="0.85" />
          ))}
          {/* Base steps */}
          <rect x="40" y="98" width="220" height="6" fill="#2b1810" opacity="0.9" />
          <rect x="20" y="104" width="260" height="10" fill="#1f1109" opacity="0.9" />
          <rect x="0"  y="114" width="300" height="16" fill="#0f0703" opacity="1" />
        </svg>
      </div>
    </div>
  );
}

/* Static, deterministic star field. */
const STAR_POSITIONS = [
  { x: 8, y: 12, size: 2, o: 0.7 },
  { x: 18, y: 25, size: 1.5, o: 0.55 },
  { x: 30, y: 8, size: 2, o: 0.8 },
  { x: 45, y: 22, size: 1.2, o: 0.5 },
  { x: 62, y: 6, size: 1.8, o: 0.65 },
  { x: 78, y: 15, size: 1.4, o: 0.55 },
  { x: 88, y: 32, size: 1.6, o: 0.6 },
  { x: 12, y: 40, size: 1.2, o: 0.4 },
  { x: 25, y: 55, size: 1.4, o: 0.5 },
  { x: 55, y: 42, size: 1.3, o: 0.55 },
  { x: 70, y: 48, size: 1.8, o: 0.75 },
  { x: 92, y: 55, size: 1.1, o: 0.45 },
  { x: 5, y: 65, size: 1.5, o: 0.6 },
  { x: 40, y: 34, size: 1.2, o: 0.5 },
  { x: 82, y: 22, size: 1.6, o: 0.6 },
];

/* Small pill-toggle used at the bottom (visual only — the whole row is
 * a button that flips the mode). */
function ToggleTrack({ on }) {
  return (
    <span
      aria-hidden
      style={{
        position: "relative",
        display: "inline-block",
        width: 40,
        height: 22,
        borderRadius: 11,
        background: on ? "rgba(232,184,75,0.28)" : "rgba(232,184,75,0.14)",
        border: "0.5px solid rgba(232,184,75,0.5)",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: on ? 22 : 2,
          width: 16,
          height: 16,
          borderRadius: 9999,
          background: "#E8B84B",
          transition: "left 0.2s ease",
        }}
      />
    </span>
  );
}

/** iter176 — Shared circular icon+label used in the Ride Mode bottom
 *  row. Extracted so Places/Background/Airport/Cigars share the exact
 *  same styling (48px hit area, gold border, 11px label). */
function RideCircle({ testid, labelTestid, label, ariaLabel, onClick, Icon, heroTextShadow }) {
  return (
    <div className="flex flex-col items-center" style={{ gap: 6 }}>
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        aria-label={ariaLabel}
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 48, height: 48, borderRadius: 9999,
          background: "rgba(23,23,23,0.6)",
          border: "1px solid rgba(232,184,75,0.35)",
          color: "#E8B84B",
          backdropFilter: "blur(6px)",
        }}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </button>
      <span
        data-testid={labelTestid}
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.06em",
          color: "rgba(245,241,232,0.7)",
          textShadow: heroTextShadow,
        }}
      >
        {label}
      </span>
    </div>
  );
}

